import { cn } from "@/src/lib/utils";

import type { RowData } from "@tanstack/react-table";

import type {
  ColumnLayoutInput,
  ColumnVisibilityInput,
  DataTableColumn,
  ParsedWidth,
  VisibleColumnLayout,
} from "./dataTable.types";

/** Les contrôles sont posés sur le fond gris de la page : shadcn les prévoit transparents. */
export const CONTROL_SURFACE_CLASS = "border-border bg-card";

export const PAGE_TITLE_CLASS = "text-muted-foreground text-[26px] font-normal";

export const CELL_ICON_CLASS = "text-muted-foreground size-5";

/** Centre la case dans sa cellule : un bouton en ligne s'aligne sur le texte, pas sur la cellule. */
export const SELECT_CHECKBOX_WRAPPER_CLASS = "flex items-center";

// shadcn ne style pas l'état indéterminé : un tiret remplace la coche que Radix affiche aussi.
export const SELECT_CHECKBOX_CLASS = cn(
  "relative size-4.5",
  // Zone de clic invisible de 10 px : un clic à peine à côté coche au lieu d'ouvrir la ligne.
  "before:absolute before:-inset-2.5 before:content-['']",
  "data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=indeterminate]:border-primary",
  "data-[state=indeterminate]:[&_svg]:hidden",
  "data-[state=indeterminate]:after:absolute data-[state=indeterminate]:after:top-1/2 data-[state=indeterminate]:after:left-1/2 data-[state=indeterminate]:after:h-0.5 data-[state=indeterminate]:after:w-2 data-[state=indeterminate]:after:-translate-x-1/2 data-[state=indeterminate]:after:-translate-y-1/2 data-[state=indeterminate]:after:rounded-full data-[state=indeterminate]:after:bg-current data-[state=indeterminate]:after:content-['']",
);

const ALIGN_CLASSES = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

const FLEXIBLE_COLUMN_MIN_PX = 200;
const PERCENT_COLUMN_MIN_PX = 120;

function parseWidth({ meta }: ColumnLayoutInput): ParsedWidth {
  const width = meta?.width;
  if (width === undefined) return undefined;

  const value = Number.parseFloat(width);
  return width.endsWith("px") ? { unit: "px", value } : { unit: "%", value };
}

/** Largeur minimale du tableau : en dessous, il défile au lieu de tronquer. Pas de `max(%, px)` par colonne : un tableau traite une telle largeur comme `auto`. */
export function getTableMinWidth(columns: ColumnLayoutInput[]): number {
  const {
    pixelTotal,
    percentTotal,
    flexibleCount,
    flexibleMin,
    percentRequired,
  } = columns.reduce(
    (totals, column) => {
      const width = parseWidth(column);

      if (width?.unit === "px") {
        return { ...totals, pixelTotal: totals.pixelTotal + width.value };
      }
      if (width?.unit === "%") {
        const minWidth = column.meta?.minWidth ?? PERCENT_COLUMN_MIN_PX;
        return {
          ...totals,
          percentTotal: totals.percentTotal + width.value / 100,
          percentRequired:
            width.value > 0
              ? Math.max(totals.percentRequired, (minWidth * 100) / width.value)
              : totals.percentRequired,
        };
      }
      return {
        ...totals,
        flexibleCount: totals.flexibleCount + 1,
        flexibleMin: Math.max(
          totals.flexibleMin,
          column.meta?.minWidth ?? FLEXIBLE_COLUMN_MIN_PX,
        ),
      };
    },
    {
      pixelTotal: 0,
      percentTotal: 0,
      flexibleCount: 0,
      flexibleMin: 0,
      percentRequired: 0,
    },
  );

  const remainingShare = 1 - percentTotal;
  const restRequired =
    remainingShare > 0
      ? (pixelTotal + flexibleCount * flexibleMin) / remainingShare
      : 0;

  return Math.ceil(Math.max(percentRequired, restRequired));
}

/** Une largeur en % est une part de tout le tableau : sans redistribuer la part des colonnes masquées, une colonne visible garde sa petite part et le tableau ne peut pas rétrécir. */
export function getVisibleColumnLayouts(
  columns: ColumnVisibilityInput[],
): VisibleColumnLayout[] {
  const hiddenShare = columns.reduce((share, column) => {
    const width = parseWidth(column);
    return !column.isVisible && width?.unit === "%"
      ? share + width.value / 100
      : share;
  }, 0);
  const scale = hiddenShare < 1 ? 1 / (1 - hiddenShare) : 1;

  return columns
    .filter(({ isVisible }) => isVisible)
    .map(({ id, meta }) => {
      const width = parseWidth({ meta });
      return width?.unit === "%" && scale !== 1
        ? { id, meta: { ...meta, width: `${width.value * scale}%` } }
        : { id, meta };
    });
}

export function getColumnLabel<TData extends RowData>(
  column: DataTableColumn<TData>,
): string {
  return column.columnDef.meta?.label ?? column.id;
}

/** Centre verticalement les éléments en ligne (icône, avatar, pastille), sinon posés sur la ligne de base du texte. */
export const BODY_CELL_CLASS = "[&>*]:align-middle";

/** Cellule vide : tiret atténué, le même partout, compteurs à 0 compris. Pas sur la colonne d'actions. */
export const EMPTY_CELL_CLASS =
  "empty:after:opacity-40 empty:after:content-['-']";

export function getColumnAlignClass<TData extends RowData>(
  column: DataTableColumn<TData>,
): string {
  return ALIGN_CLASSES[column.columnDef.meta?.align ?? "left"];
}
